import {
  expandAustraliaAgeGroups,
  expandGradeLevel,
  parseAgeGroupValues,
} from "./helpers";

type LaunchedMultiCollection = {
  id?: string | null;
  grade?: string | null;
} | null;

type ResolveMcAnalyticsGradeInput = {
  /** The lesson's own grade range - ageGroup (AU) or gradeLevel (NA), a stringified array. */
  lessonGrade?: string | null;
  /** Every multi-collection the lesson belongs to. */
  multiCollections?: readonly LaunchedMultiCollection[] | null;
  /** The multi-collection the teacher actually launched from, if the surface knows it. */
  mcId?: string | null;
  /** The launched card's grade, carried on the link when the lesson was opened by URL. */
  launchedGrade?: string | null;
};

/**
 * Both regions can express a span as a single combined label rather than as separate entries -
 * "Grade 3-4" and "Grade K-3" in North America, "Year 3-4" and "Kindy/Reception - Year 2" in
 * Australia. Counting those raw would read a two-year span as one grade, so expand through the
 * existing helpers first. Each expander passes the other region's labels through untouched, and
 * discrete values are unaffected.
 */
const expandGradeValues = (value?: string | null): string[] =>
  expandAustraliaAgeGroups(
    parseAgeGroupValues(value)
      .filter((entry): entry is string => typeof entry === "string")
      .flatMap((entry) => expandGradeLevel(entry)),
  );

/**
 * The card a lesson was launched from, where it can be named with certainty.
 *
 * Only the multi-collection card grids pass `mcId` through, because only they have a card to
 * name. A lesson opened from its own URL, from a theme collection, or from a dashboard program
 * card arrives without one. Belonging to exactly one multi-collection settles it regardless: that
 * sole collection is the only card the lesson could have been launched from. Anything less
 * certain - several collections, or an `mcId` that names none of them - stays unresolved.
 */
const resolveLaunchedCollection = (
  multiCollections?: readonly LaunchedMultiCollection[] | null,
  mcId?: string | null,
): LaunchedMultiCollection => {
  const collections = (multiCollections ?? []).filter(Boolean);
  if (mcId) return collections.find((mc) => mc?.id === mcId) ?? null;
  return collections.length === 1 ? collections[0] : null;
};

/**
 * OP-3092: a lesson that spans several grades sits on one multi-collection card per grade, so the
 * lesson's own grade range cannot say which grade the teacher was actually teaching. When such a
 * lesson belongs to a single-grade card, report that card's grade instead.
 *
 * Returns undefined whenever the rule does not apply, so the caller falls back to the lesson's
 * range: single-grade lessons, an ambiguous launch context, and cards that themselves span
 * several grades are all left exactly as they were.
 */
export const resolveMcAnalyticsGrade = ({
  lessonGrade,
  multiCollections,
  mcId,
  launchedGrade,
}: ResolveMcAnalyticsGradeInput): string | undefined => {
  const lessonGrades = expandGradeValues(lessonGrade);

  // The rule is scoped to multi-grade lessons only.
  if (lessonGrades.length < 2) return undefined;

  // A grade carried on the link names the card outright, so it needs no lookup. That matters:
  // multiCollections only reports collections joined to the lesson directly, and a lesson can sit
  // under a grade through a theme instead, where an id would match nothing and fall back.
  //
  // Only accept a grade the lesson actually carries, so a hand-edited link cannot invent one.
  const carried = expandGradeValues(launchedGrade);
  if (carried.length === 1 && lessonGrades.includes(carried[0])) {
    return JSON.stringify(carried);
  }

  const launched = resolveLaunchedCollection(multiCollections, mcId);
  const mcGrades = expandGradeValues(launched?.grade);

  // Only single-select cards identify a grade unambiguously.
  if (mcGrades.length !== 1) return undefined;

  // Match the shape `grade` already uses on these events: a stringified array.
  return JSON.stringify(mcGrades);
};
